import {
	isLocalStorageAvailable,
	generateExampleFromSchema
} from 'components/utility/utils';

const localStorageAvailable = isLocalStorageAvailable();

// Return true if we are in a new Flow creation mode
// Exported for testing purposes only
export function isNewFlow(flowId) {
	return flowId === '$$_new_flow';
}

/**
 * Factory function that creates Parameters Manager. It can be used for
 * constructing a flow parameters set that is used in the Flow Debugger.
 * Parameters Manager works with localStorage, when available, to save and
 * later reuse the constructed parameter set. Those sets are managed per
 * flow, per project. Note: Second parameter will be needed only until we
 * complete this https://jira.axway.com/browse/RDPP-6574.
 */
export function createParametersManager(props, useCache = localStorageAvailable) {
	const { flow, flowId, projectName } = props;

	const itemKey = `flow-debug-${projectName}-${flowId}`;

	// Replicate the state in memory so the parameter manager still works when
	// localStorage is not available.
	const state = {
		parameters: getExample()
	};

	const parametersManager = {
		/**
		 * If true the parameter manager is working with localStorage, otherwise
		 * it just work with the in memory state.
		 */
		useCache,
		/**
		 *
		 * @returns {boolean} returns true if manage only the full set of
		 * 	parameters for the flow.
		 */
		supportsParamsOnly: () => {
			// Our flowschema validation allows the flows to not have parameter.
			// However if they do, the properties are required.
			return !!(flow && flow.parameter && flow.parameter.properties.params);
		},
		/**
		 * Initialize the 'useAllParams' flag and the cache if it is empty.
		 *
		 * @returns {Object} returns the parameterManager.
		 */
		init: () => {
			let params;
			let useAllParams = !parametersManager.supportsParamsOnly();

			// Check do we have something in the cache from previous usage
			// if we use it at all.
			const cache = parametersManager.getCache();
			if (cache) {
				// we have something in the cache
				useAllParams = cache.useAllParams;
				params = useAllParams
					? cache.parameters : cache.parameters.params;

				// update memory state with cached data
				state.parameters = cache.parameters;
			} else {
				// Cache is empty. Generate examples
				params = getExample(!useAllParams);
				// Init the cache initially helps in the use case when we
				// create a new flow and directly save (no execute). Then
				// in sagas we just use the state from '$$_new_flow' id and
				// reassign it to the real id. No need to check does
				// '$$_new_flow' state exist or not.
				writeInLocalStorage(getExample(), useAllParams);
			}

			return {
				parametersManager,
				useAllParams,
				parameters: ensureStringifiedParams(params)
			};
		},
		/**
		 * Switch from one set of parameters to the other and updates the
		 * cache.
		 *
		 * @param {string} currentParameters - the currently used parameters.
		 * @param {boolean} useAllParams - if true switch to all parameters,
		 * 	otherwise switch to 'params' section only.
		 * @returns {string} stringified parameters
		 */
		toggleParameters: (currentParameters, useAllParams) => {
			let params;
			try {
				const objParams = ensureObjectParams(currentParameters);
				if (useAllParams) {
					state.parameters.params = objParams;
					params = ensureStringifiedParams(state.parameters);
				} else {
					state.parameters = objParams;
					params = ensureStringifiedParams(state.parameters.params);
				}
				writeInLocalStorage(state.parameters, useAllParams);
				return params;
			} catch (ex) {
				return getStringifiedExample(!useAllParams);
			}
		},

		/**
		 * Generates fresh parameters example and update the cache
		 * 	accordingly.
		 *
		 * @param {boolean} useAllParams - if true generates example for all
		 * 	parameters, otherwise generates example for 'params' section.
		 */
		getFreshParameters: (useAllParams) => {
			const freshParams = getStringifiedExample(!useAllParams);
			parametersManager.cache(freshParams, useAllParams);
			return freshParams;
		},

		/**
		 * Gets cached information. Works only when localStorage is available.
		 * In case of an exception reinitialize the cache and flush what is in
		 * memory.
		 *
		 * @returns {Object|undefined} return the cached information or
		 * 	'undefined' if nothing is stored previously for the
		 *	projectName/flowId.
		 */
		getCache: () => {
			if (!parametersManager.useCache) {
				return;
			}
			try {
				return JSON.parse(localStorage.getItem(itemKey));
			} catch (ex) {
				const useAllParams = !parametersManager.supportsParamsOnly();
				const params = getExample();
				// clear memory and reinit localStorage
				state.parameters = params;
				writeInLocalStorage(params, useAllParams);
				return {
					parameters: params,
					useAllParams
				};
			}
		},
		/**
		 * Stores the parameters into memory and into localStorage.
		 *
		 * @param {string} parameters - stringifed parameters to store
		 * @param {boolean} useAllParams - a flag stored into the localStorage
		 * 	that denotes if all parameters are in use or only the 'params'
		 * 	section.
		 */
		cache: (parameters, useAllParams) => {
			const objParams = ensureObjectParams(parameters);
			if (useAllParams) {
				state.parameters = objParams;
			} else {
				state.parameters.params = objParams;
			}
			writeInLocalStorage(state.parameters, useAllParams);
		},
		/**
		 * @returns {Object} returns the in memory state
		 */
		getState: () => {
			return state;
		}
	};
	return parametersManager;

	function writeInLocalStorage(parameters, useAllParams) {
		if (parametersManager.useCache) {
			localStorage
				.setItem(
					itemKey,
					JSON.stringify({
						parameters: parameters,
						useAllParams: useAllParams
					}));
		}
	}

	function getExample(paramsOnly) {
		return generateExampleFromSchema(flow.parameter, paramsOnly);
	}

	function getStringifiedExample(paramsOnly) {
		return JSON.stringify(
			generateExampleFromSchema(flow.parameter, paramsOnly), null, 2
		);
	}

	function ensureStringifiedParams(params) {
		return typeof params === 'string'
			? params
			: JSON.stringify(params, null, 2);
	}

	function ensureObjectParams(params) {
		return typeof params === 'string'
			? JSON.parse(params)
			: params;
	}
}
